import { CaretRightIcon, SparkleIcon } from '@phosphor-icons/react';
import * as Dialog from '@radix-ui/react-dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { motion } from 'motion/react';
import React, { useEffect, useState } from 'react';
import CONSTANTS from '../../Constants';
import { useStores } from '../../Hooks';
import { useGetSkins } from '../../Hooks/useGetSkins';
import { getUrlForEnvironmentBridge } from '../../Utils/environment';
import Button, { ButtonProps } from '../ui/Button/Button';
import { Skeleton } from '../ui/Skeleton';
import { Typography } from '../ui/Typography/Typography';
import styles from './try-on-button.module.scss';

const IFRAME_EXIT_CODE = '00001';
const IFRAME_FIT_RESPONSE_CODE = '00002';

export type TryOnButtonVariant = 'default' | 'outfit' | 'icon';

interface TryOnButtonProps extends Omit<ButtonProps, 'variant'> {
    garments: {
        garmentId: string;
        variant?: {
            color?: string;
            size?: string;
            fit?: string;
            style?: string;
        };
        price?: string;
        availableSizes?: string[];
    }[];
    partnerId: string;
    text?: string;
    variant?: TryOnButtonVariant;
    onSizingResponse?: (response: TryOnSizingResponse) => void;
}

export interface TryOnSizingResponse {
    text?: string;
    larger_size?: string;
    smaller_size?: string;
}

interface PartnerConfig {
    avatars_enabled: boolean;
    background_removal_enabled: boolean;
    created?: string;
    created_by?: string;
    max_zoom_level?: number;
    partner_id?: string;
    photocapture_enabled: boolean;
    photoupload_enabled: boolean;
    posecount?: number;
    rate_limit_per_minute?: number;
    spree_accounts_enabled?: boolean;
}

const PARTNER_CONFIG: PartnerConfig = {
    spree_accounts_enabled: true,
    avatars_enabled: true,
    background_removal_enabled: false,
    photocapture_enabled: false,
    photoupload_enabled: true,
};

const getPublicKey = (): string => {
    if (CONSTANTS.IS_DEV) {
        // protea
        return 'f3267114-9b01-0000-0100-b23099d74e92';
    }
    if (CONSTANTS.IS_STAGE) {
        return '3b937314-9b01-0000-0100-3939fdcdf023';
    }
    return '095ea92a-9b01-0000-0100-d796e1d7ea6d';
};

export const TryOnButton = ({
    garments,
    text = 'See How This Fits You',
    partnerId,
    onSizingResponse,
    variant = 'default',
    ...props
}: TryOnButtonProps) => {
    const [open, setOpen] = useState(false);
    const { languageStore } = useStores();
    const { isPending } = useGetSkins();

    const handleIframeMessage = (event: any) => {
        if (event.data.code === IFRAME_EXIT_CODE) {
            setOpen(false);
            document.body.classList.remove('no-scroll');
        }
        //
        if (event.data.code === IFRAME_FIT_RESPONSE_CODE && onSizingResponse) {
            const response: TryOnSizingResponse = event.data.messageData;
            onSizingResponse(response);
        }
    };

    useEffect(() => {
        window.addEventListener('message', handleIframeMessage);

        return () => window.removeEventListener('message', handleIframeMessage);
    }, []);

    const getTryAccountUrl = () => getUrlForEnvironmentBridge(CONSTANTS.BRIDGE_URL);

    if (isPending) {
        return <Skeleton height={42} width={120} />;
    }

    return (
        <Dialog.Root modal={true} open={open} onOpenChange={setOpen}>
            <Dialog.Trigger asChild>
                {variant === 'outfit' ? (
                    <Button aria-label={text} className={styles.outfitButton} {...props}>
                        <SparkleIcon size={24} />
                        <span className={styles.outfitButtonText}>
                            <Typography variant="bodyBold" component="span">
                                Try This Look
                            </Typography>
                            <Typography color="secondary" component="span">
                                Bring the look to life in the virtual fitting room.
                            </Typography>
                        </span>
                        <CaretRightIcon size={24} />
                    </Button>
                ) : variant === 'icon' ? (
                    <IconTryOnButton text={text} {...props} />
                ) : (
                    <Button aria-label={text} className={styles.button} {...props}>
                        <span>{text}</span>
                    </Button>
                )}
            </Dialog.Trigger>
            <Dialog.Portal>
                <Dialog.Overlay className={styles.overlay} />
                <Dialog.Content className={styles.content}>
                    <VisuallyHidden>
                        <Dialog.Title> SpreeAI Try On Web SDK</Dialog.Title>
                        <Dialog.Description> SpreeAI Try On Web SDK</Dialog.Description>
                    </VisuallyHidden>

                    <iframe
                        className={styles.iframe}
                        title="Try On"
                        src={`${getTryAccountUrl()}/?garments=${encodeURIComponent(
                            JSON.stringify(garments)
                        )}&partnerId=${partnerId}&lng=${
                            languageStore.currentLanguage
                        }&partnerConfig=${encodeURIComponent(
                            JSON.stringify(PARTNER_CONFIG)
                        )}&clientId=${getPublicKey()}&enableProteaGarments=true&enableAddToCart=false&enableSizingRecommendation=false`}
                        sandbox="allow-scripts allow-forms allow-same-origin allow-top-navigation allow-popups allow-downloads"
                        allow="web-share"
                    />
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};

const ICON_BUTTON_EASE = [0.16, 1, 0.3, 1] as const;

interface IconTryOnButtonProps extends Omit<ButtonProps, 'variant'> {
    text: string;
}

const IconTryOnButton = React.forwardRef<HTMLButtonElement, IconTryOnButtonProps>(
    ({ text, ...props }, ref) => {
        const [active, setActive] = useState(false);

        return (
            <Button
                ref={ref}
                aria-label={text}
                className={styles.iconButton}
                onMouseEnter={() => setActive(true)}
                onMouseLeave={() => setActive(false)}
                {...props}
            >
                <SparkleIcon size={24} className={styles.iconButtonIcon} />
                <motion.span
                    className={styles.iconButtonText}
                    initial={false}
                    animate={{
                        width: active ? 'auto' : 0,
                        opacity: active ? 1 : 0,
                        marginLeft: active ? 8 : 0,
                    }}
                    transition={{
                        width: { duration: 0.45, ease: ICON_BUTTON_EASE },
                        opacity: { duration: 0.25, ease: 'easeInOut' },
                        marginLeft: { duration: 0.45, ease: ICON_BUTTON_EASE },
                    }}
                >
                    {text}
                </motion.span>
            </Button>
        );
    }
);

IconTryOnButton.displayName = 'IconTryOnButton';
